import { toAbsolutePublicUrl } from "../../../services/mediaLibraryServices";

export const FALLBACK_PROJET_IMAGE = "/img/no-image.jpg";

/* -------------------------------------------------------------------------- */
/*                                   Utils                                    */
/* -------------------------------------------------------------------------- */

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function ensureArray(value) {
  return Array.isArray(value) ? value : [];
}

export function toBoolean(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1"
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Texte                                     */
/* -------------------------------------------------------------------------- */

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(
  value = "",
  maxLength = 180,
) {
  const text = String(value || "").trim();

  if (text.length <= maxLength) {
    return text;
  }

  return `${text
    .slice(0, maxLength - 1)
    .trim()}…`;
}

export function normalizeSearchText(
  value = "",
) {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

/* -------------------------------------------------------------------------- */
/*                                  Dates                                     */
/* -------------------------------------------------------------------------- */

export function parseDate(value) {
  if (!value) return null;

  const date = new Date(value);

  return Number.isNaN(
    date.getTime(),
  )
    ? null
    : date;
}

export function formatDate(value) {
  const date = parseDate(value);

  if (!date) return "";

  return date.toLocaleDateString(
    "fr-FR",
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    },
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Image                                     */
/* -------------------------------------------------------------------------- */

export function resolveProjetImage(
  value,
) {
  const image = String(
    value || "",
  ).trim();

  /*
   * Pas d'image :
   * image locale du front utilisée comme fallback.
   */
  if (!image) {
    return FALLBACK_PROJET_IMAGE;
  }

  /*
   * URL absolue :
   * on ne touche à rien.
   *
   * Ex :
   * https://api.site.fr/uploads/...
   * https://res.cloudinary.com/...
   */
  if (/^https?:\/\//i.test(image)) {
    return image;
  }

  /*
   * Images provenant des uploads du serveur.
   *
   * IMPORTANT :
   * /uploads/... ne doit PAS être interprété
   * comme un chemin relatif au frontend.
   *
   * toAbsolutePublicUrl() ajoutera l'URL de l'API.
   */
  if (
    image.startsWith("/uploads/") ||
    image.startsWith("uploads/")
  ) {
    return (
      toAbsolutePublicUrl(image) ||
      FALLBACK_PROJET_IMAGE
    );
  }

  /*
   * Médias éventuellement stockés avec d'autres
   * chemins serveur.
   *
   * On essaye toAbsolutePublicUrl().
   */
  if (!image.startsWith("/")) {
    return (
      toAbsolutePublicUrl(image) ||
      FALLBACK_PROJET_IMAGE
    );
  }

  /*
   * Chemins locaux du frontend :
   *
   * /img/...
   * /assets/...
   *
   * Ils doivent rester relatifs au front.
   */
  return image;
}

/* -------------------------------------------------------------------------- */
/*                             Infos projet                                   */
/* -------------------------------------------------------------------------- */

export function getProjetDate(
  projet,
) {
  return (
    projet?.updated_at ||
    projet?.created_at ||
    ""
  );
}

export function isActiveProjet(
  projet,
) {
  return toBoolean(
    projet?.is_active,
  );
}

/* -------------------------------------------------------------------------- */
/*                          Normalisation projet                              */
/* -------------------------------------------------------------------------- */

export function normalizeProjet(
  projet,
) {
  const title =
    projet?.titre ||
    "Projet";

  const descriptionRaw =
    projet?.description ||
    "";

  const descriptionText =
    stripHtml(
      descriptionRaw,
    );

  const dateValue =
    getProjetDate(
      projet,
    );

  const dateLabel =
    formatDate(
      dateValue,
    );

  const updatedLabel =
    projet?.updated_at
      ? formatDate(
          projet.updated_at,
        )
      : "";

  const createdLabel =
    projet?.created_at
      ? formatDate(
          projet.created_at,
        )
      : "";

  /*
   * Résolution de l'image.
   */
  const image =
    resolveProjetImage(
      projet?.image_projet_url,
    );

  const normalized = {
    id: projet?.id,

    raw: projet,

    /* Identité */

    title,

    /* Description */

    descriptionRaw,
    descriptionText,

    excerpt:
      truncateText(
        descriptionText,
        190,
      ),

    /* Dates */

    dateValue,
    dateLabel,
    updatedLabel,
    createdLabel,

    /* Image */

    image,

    imageAlt: title,

    /* Tri */

    sortDate:
      parseDate(
        dateValue,
      )?.getTime() || 0,
  };

  /* ------------------------------------------------------------------------ */
  /* Index recherche                                                         */
  /* ------------------------------------------------------------------------ */

  normalized.searchIndex =
    normalizeSearchText(
      [
        normalized.title,
        normalized.descriptionText,
        normalized.dateLabel,
      ]
        .filter(Boolean)
        .join(" "),
    );

  return normalized;
}

/* -------------------------------------------------------------------------- */
/*                                   Tri                                      */
/* -------------------------------------------------------------------------- */

export function sortProjets(
  items,
) {
  return [...items].sort(
    (a, b) => {
      if (
        a.sortDate !==
        b.sortDate
      ) {
        return (
          b.sortDate -
          a.sortDate
        );
      }

      return normalizeSearchText(
        a.title,
      ).localeCompare(
        normalizeSearchText(
          b.title,
        ),
        "fr",
      );
    },
  );
}